/**
 * Resolves the visitor's country from their browser timezone.
 *
 * Timezone is a better location signal than locale: a visitor in Karachi with
 * their phone set to English reports locale "en-US" but timezone
 * "Asia/Karachi". We want where they are, not what language they read.
 *
 * This is an approximation a VPN or a traveller reports where the device
 * thinks it is. That is acceptable for analytics; it is not identity, and no
 * IP address is collected.
 */

/**
 * IANA timezone to ISO 3166-1 alpha-2. Covers the zones that actually appear
 * in traffic; anything unmapped falls back to the region in the locale, then
 * to "unknown", so the table does not need to be exhaustive.
 */
const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  // South and Central Asia
  "Asia/Karachi": "PK",
  "Asia/Kolkata": "IN",
  "Asia/Calcutta": "IN",
  "Asia/Dhaka": "BD",
  "Asia/Kathmandu": "NP",
  "Asia/Colombo": "LK",
  "Asia/Kabul": "AF",
  "Asia/Tashkent": "UZ",
  // Middle East
  "Asia/Dubai": "AE",
  "Asia/Riyadh": "SA",
  "Asia/Qatar": "QA",
  "Asia/Kuwait": "KW",
  "Asia/Bahrain": "BH",
  "Asia/Muscat": "OM",
  "Asia/Baghdad": "IQ",
  "Asia/Tehran": "IR",
  "Asia/Jerusalem": "IL",
  "Asia/Amman": "JO",
  "Asia/Beirut": "LB",
  "Asia/Damascus": "SY",
  // East and Southeast Asia
  "Asia/Shanghai": "CN",
  "Asia/Hong_Kong": "HK",
  "Asia/Taipei": "TW",
  "Asia/Tokyo": "JP",
  "Asia/Seoul": "KR",
  "Asia/Singapore": "SG",
  "Asia/Bangkok": "TH",
  "Asia/Jakarta": "ID",
  "Asia/Manila": "PH",
  "Asia/Kuala_Lumpur": "MY",
  "Asia/Ho_Chi_Minh": "VN",
  "Asia/Saigon": "VN",
  // Europe
  "Europe/London": "GB",
  "Europe/Dublin": "IE",
  "Europe/Paris": "FR",
  "Europe/Berlin": "DE",
  "Europe/Madrid": "ES",
  "Europe/Rome": "IT",
  "Europe/Amsterdam": "NL",
  "Europe/Brussels": "BE",
  "Europe/Vienna": "AT",
  "Europe/Zurich": "CH",
  "Europe/Stockholm": "SE",
  "Europe/Oslo": "NO",
  "Europe/Copenhagen": "DK",
  "Europe/Helsinki": "FI",
  "Europe/Warsaw": "PL",
  "Europe/Prague": "CZ",
  "Europe/Budapest": "HU",
  "Europe/Bucharest": "RO",
  "Europe/Athens": "GR",
  "Europe/Lisbon": "PT",
  "Europe/Moscow": "RU",
  "Europe/Kyiv": "UA",
  "Europe/Kiev": "UA",
  "Europe/Istanbul": "TR",
  // Americas
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Phoenix": "US",
  "America/Los_Angeles": "US",
  "America/Anchorage": "US",
  "Pacific/Honolulu": "US",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "America/Edmonton": "CA",
  "America/Winnipeg": "CA",
  "America/Halifax": "CA",
  "America/Mexico_City": "MX",
  "America/Sao_Paulo": "BR",
  "America/Buenos_Aires": "AR",
  "America/Argentina/Buenos_Aires": "AR",
  "America/Santiago": "CL",
  "America/Bogota": "CO",
  "America/Lima": "PE",
  // Africa
  "Africa/Cairo": "EG",
  "Africa/Lagos": "NG",
  "Africa/Nairobi": "KE",
  "Africa/Johannesburg": "ZA",
  "Africa/Casablanca": "MA",
  "Africa/Accra": "GH",
  "Africa/Algiers": "DZ",
  "Africa/Tunis": "TN",
  // Oceania
  "Australia/Sydney": "AU",
  "Australia/Melbourne": "AU",
  "Australia/Brisbane": "AU",
  "Australia/Perth": "AU",
  "Australia/Adelaide": "AU",
  "Pacific/Auckland": "NZ",
};

/** The region in a locale tag, e.g. "en-GB" -> "GB". */
function regionFromLocale(): string | null {
  try {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    // Intl.Locale is not in older Safari, so parse the tag as a fallback.
    const region =
      typeof Intl.Locale === "function"
        ? new Intl.Locale(locale).region
        : locale.split("-")[1];
    return region && /^[A-Z]{2}$/.test(region) ? region : null;
  } catch {
    return null;
  }
}

/**
 * Best-effort ISO country code for the current visitor, or "unknown".
 * Synchronous and offline no geolocation request, no IP lookup.
 */
export function detectCountry(): string {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const mapped = timezone ? TIMEZONE_TO_COUNTRY[timezone] : undefined;
    if (mapped) return mapped;

    // An unmapped timezone still tells us something via the locale but only
    // as a fallback, since locale reflects language preference, not location.
    const region = regionFromLocale();
    if (region) return region;
  } catch {
    // Intl can throw in exotic environments; fall through.
  }
  return "unknown";
}
